import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './ServicesPage.css';
import {
  FaIndustry,
  FaWrench,
  FaBurn,
  FaPaintRoller,
  FaBuilding,
  FaTools,
  FaLayerGroup,
  FaHardHat,
  FaCogs,
  FaTruck,
  FaChevronDown,
} from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

const services = [
  {
    title: 'Miscellaneous Metals',
    description:
      'Stair systems, railings, guards, fencing, brackets, and one-off site metal for industrial, commercial, and public work—complementary to the main structural work on the job, with all fabrication to your engineered drawings, approved submittals, and agreed field conditions.',
    features: [
      'Custom steel staircases, stringers, and landings',
      'Handrails, stair guards, and functional railings',
      'Perimeter, pathway, and security fencing',
      'Custom brackets, minor embeds, repairs, and one-off fabrications',
      'Built to your engineered shop drawings, revision-controlled issue sets, and site verification as required'
    ],
    icon: <FaTools />
  },
  {
    title: 'Structural',
    description:
      'Primary structural steel—beams, columns, frames, and connections—shop-fabricated and welded to your engineered drawings, sealed calculations where applicable, and project specifications. Work follows the issued drawing set and revisions you approve—no scope outside what is shown on the engineer’s contract documents.',
    features: [
      'Beams, columns, and load-resisting frames',
      'Mezzanines, platforms, and building-level structural supports',
      'Connection plates, base plates, and defined embeds per the structural drawing set',
      'Bracing, misc. structural members, and built-up assemblies to approved shop and erection drawings',
      'Industrial and commercial structural work to issued-for-construction and contract documents'
    ],
    icon: <FaLayerGroup />
  },
  {
    title: 'Installation',
    description:
      'On-site delivery and install for the metal we fabricate—structural, miscellaneous, and architectural—erected and fitted to your engineered drawings, site layout, anchor plans, and agreed field tolerances.',
    features: [
      'Erection and field installation to agreed scope and schedule',
      'Coordination with your superintendent, crane, and trades',
      'Field fit-up, shimming, and alignment for fabricated components',
      'Rigging and lift planning in line with site safety requirements',
      'Handoff and walkthrough for completed metal scope'
    ],
    icon: <FaHardHat />
  },
  {
    title: 'Architectural Metals',
    description:
      'Design-forward metal that completes the building—feature screens, canopies, entrance work, and custom assemblies built to your engineered and architectural drawing sets, intent, and finish schedules.',
    features: [
      'Feature screens, grills, and decorative metal',
      'Canopies, sun shades, and entrance metalwork',
      'Façade accents and custom architectural assemblies',
      'Design-forward railing and guard systems',
      'Shop detailing, samples, and finish coordination'
    ],
    icon: <FaBuilding />
  },
  {
    title: 'Metal Supply',
    description:
      'Limited metal supply across common grades and shapes—steel, stainless, aluminum, and more—cut to length, without a full fabrication package. Minimum order $1,000.',
    features: [
      'All common metals and shapes',
      'Cut to length',
      'Minimum order $1,000',
      'Supply-only orders welcome'
    ],
    icon: <FaTruck />
  },
  {
    title: 'Custom Metal Fabrication',
    description:
      'Precision fabrication for custom metal structures, assemblies, and components—cut, fit, and welded to your engineered drawings and approved details.',
    features: [
      'Precision cutting and shaping',
      'Custom metal structures',
      'Complex assemblies',
      'Quality control at every step'
    ],
    icon: <FaIndustry />
  },
  {
    title: 'Production Fabrication',
    description:
      'Repeat and production fabrication for OEMs, facilities, and industrial accounts—same parts, consistent quality, and reliable turnaround for ongoing and batch work.',
    features: [
      'Repeat parts and production runs',
      'Cut, fit, and weld to approved drawings',
      'Consistent quality across batches',
      'Reliable scheduling for recurring orders'
    ],
    icon: <FaCogs />
  },
  {
    title: 'Welding Services',
    description:
      'Professional welding for all metals, including advanced techniques and structural work, performed to your engineered drawings, WPS, and project weld requirements.',
    features: [
      'MIG, TIG, and Stick welding',
      'Aluminum and stainless steel expertise',
      'Structural welding',
      'Quality assurance testing'
    ],
    icon: <FaWrench />
  },
  {
    title: 'Plasma Cutting',
    description:
      'OEM and production plasma cutting—CNC cut-to-print parts, nesting, and plate processing for manufacturers and industrial accounts that need clean, accurate blanks on schedule.',
    features: [
      'CNC plasma from your DXF/DWG and drawings',
      'OEM and production part runs',
      'Thick and thin plate capability',
      'Fast turnaround with minimal heat distortion'
    ],
    icon: <FaBurn />,
    linkTo: '/plasma-cutting',
    linkLabel: 'View Plasma Cutting'
  },
  {
    title: 'Metal Finishing',
    description:
      'Finishing and coatings applied to parts per your engineered drawings, specification sections, and approved color or sample submittals.',
    features: [
      'Powder coating',
      'Anodizing',
      'Polishing and buffing',
      'Custom finishes'
    ],
    icon: <FaPaintRoller />
  }
];

const ServicesPage = () => {
  const [openIndexes, setOpenIndexes] = useState(() => new Set());

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
      easing: 'ease-out'
    });
  }, []);

  const toggleService = (index) => {
    setOpenIndexes((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="services-page">
      <div className="services-hero">
        <div className="services-overlay"></div>
        <div className="hero-content" data-aos="fade-up">
          <h1 className="gradient-text">OUR SERVICES</h1>
          <p>Comprehensive Metal Fabrication Solutions</p>
        </div>
      </div>
      <div className="section-divider" data-aos="zoom-in"></div>
      <section className="services-main">
        <div className="services-container">
          <div className="services-intro" data-aos="fade-up">
            <h2>Expert Metal Fabrication Services</h2>
            <p>From concept to completion, we provide comprehensive metal fabrication solutions tailored to your specific needs. Our state-of-the-art facility and experienced team ensure the highest quality results for every project.</p>
          </div>
          <div className="section-divider" data-aos="zoom-in"></div>
          <div className="services-accordion" data-aos="fade-up">
            {services.map((service, index) => {
              const isOpen = openIndexes.has(index);
              const panelId = `service-panel-${index}`;
              const headerId = `service-header-${index}`;

              return (
                <div
                  className={`service-accordion-item${isOpen ? ' is-open' : ''}`}
                  key={service.title}
                >
                  <button
                    type="button"
                    id={headerId}
                    className="service-accordion-header"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleService(index)}
                  >
                    <span className="service-accordion-icon" aria-hidden="true">
                      {service.icon}
                    </span>
                    <span className="service-accordion-title">{service.title}</span>
                    <FaChevronDown className="service-accordion-chevron" aria-hidden="true" />
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    className="service-accordion-panel"
                    hidden={!isOpen}
                  >
                    <div className="service-accordion-body">
                      <p>{service.description}</p>
                      <ul className="service-features-list">
                        {service.features.map((feature) => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                      {service.linkTo ? (
                        <Link to={service.linkTo} className="learn-more-btn">
                          {service.linkLabel || 'Learn More'}
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="section-divider" data-aos="zoom-in"></div>
          <div className="process-section" data-aos="fade-up">
            <h2>Our Process</h2>
            <div className="process-steps">
              <div className="process-step" data-aos="fade-up" data-aos-delay="100">
                <div className="step-number">1</div>
                <h3>Consultation</h3>
                <p>We discuss your requirements and provide expert guidance</p>
              </div>
              <div className="process-step" data-aos="fade-up" data-aos-delay="200">
                <div className="step-number">2</div>
                <h3>Design</h3>
                <p>Our team creates detailed plans and specifications</p>
              </div>
              <div className="process-step" data-aos="fade-up" data-aos-delay="300">
                <div className="step-number">3</div>
                <h3>Fabrication</h3>
                <p>Skilled craftsmen bring your project to life</p>
              </div>
              <div className="process-step" data-aos="fade-up" data-aos-delay="400">
                <div className="step-number">4</div>
                <h3>Quality Control</h3>
                <p>Rigorous testing ensures perfect results</p>
              </div>
            </div>
          </div>
          <div className="section-divider" data-aos="zoom-in"></div>
          <div className="cta-section" data-aos="fade-up">
            <h2>Ready to Start Your Project?</h2>
            <p>Contact us today for a free consultation and quote</p>
            <button className="cta-button">Get in Touch</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
